import { tradeCoverImage, tradesMissingCoverImage } from '@/data/constants';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * PROJECT COVER IMAGE — ONE resolver used by every surface.
 * ───────────────────────────────────────────────────────────────────────────
 * Every project must ALWAYS render a real photograph. The old generic gray
 * "tools" tile is never acceptable output, so this function can never return
 * an empty/undefined src.
 *
 * Priority (exactly as specified by the product requirement):
 *   1. UPLOAD   — the homeowner's own photo. Index 0 of `photos` is the cover;
 *                 the poster can promote any photo to index 0 with
 *                 "Make cover" in the editor. Remaining photos stay in the
 *                 gallery.
 *   2. TRADE    — a professional cover photo for that trade/category, resolved
 *                 through TRADE_IMAGES + TRADE_IMAGE_ALIASES so SEO slugs and
 *                 synonyms (windows-doors, pool, fencing, tile, masonry…) also
 *                 land on a real image.
 *   3. FALLBACK — the branded marketplace cover. Only reachable if a brand-new
 *                 trade slug is introduced without adding an image, which
 *                 `coverCoverageReport()` surfaces in the admin health check.
 *
 * Consumers: JobCard (marketplace / homepage / search / featured / similar),
 * JobDetail hero gallery, OwnerDashboard, HomeownerResponses, AdminModeration,
 * and the JobPosting schema image.
 */

/** Branded marketplace cover — priority #3, never a gray icon. */
export const PROJECT_PLACEHOLDER =
  'https://d64gsuwffb70l.cloudfront.net/6aa098f512ab50b0678fe787_1789100304318_731074ff.jpg';

export type CoverSource = 'upload' | 'trade' | 'fallback';

export interface ProjectCover {
  /** Always a usable absolute image URL. */
  src: string;
  /** True only when the image is a photo the poster actually uploaded. */
  isRealPhoto: boolean;
  /** Which tier of the priority chain produced `src`. */
  source: CoverSource;
}

export interface CoverInput {
  photos?: string[] | null;
  trade?: string | null;
  /** Optional: some views carry the category under a different column. */
  category?: string | null;
}

/** Accepts only a non-empty absolute http(s) URL, so a stray '' or null can't win. */
const usable = (value: unknown): value is string =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim());

export const coverImageFor = (listing: CoverInput | null | undefined): ProjectCover => {
  const photos = Array.isArray(listing?.photos) ? listing!.photos!.filter(usable) : [];

  // 1 — homeowner upload
  if (photos.length > 0) return { src: photos[0].trim(), isRealPhoto: true, source: 'upload' };

  // 2 — trade / category library
  const trade = tradeCoverImage(listing?.trade) || tradeCoverImage(listing?.category);
  if (usable(trade)) return { src: trade, isRealPhoto: false, source: 'trade' };

  // 3 — branded marketplace cover
  return { src: PROJECT_PLACEHOLDER, isRealPhoto: false, source: 'fallback' };
};

/** Convenience for schema.org / og:image where only a URL is needed. */
export const coverUrlFor = (listing: CoverInput | null | undefined): string =>
  coverImageFor(listing).src;

/**
 * Health check used by the admin launch checks: any trade listed here would
 * render the generic marketplace cover instead of a category photo.
 */
export const coverCoverageReport = () => {
  const missing = tradesMissingCoverImage();
  return { ok: missing.length === 0, missingTrades: missing };
};
